import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Checkbox,
    FormControlLabel,
    Grid,
    Divider,
    Stack,
    Button,
    useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useGetRolesPermission } from '../../hooks/roles/useGetRolesPermission';
import { useCreateRoles } from '../../hooks/roles/useCreateRoles';
import { useUpdateRoles } from '../../hooks/roles/useUpdateRoles';

const FormAddEditRoles = ({ currentData, isEdit = false }) => {
    const { i18n } = useTranslation();
    const theme = useTheme();
    const lng = i18n.language || 'en';
    const isArabic = lng === 'ar';

    const { Permission, isLoading } = useGetRolesPermission();
    const { createRoles, isPending: isCreating } = useCreateRoles();
    const { editRoles, isUpdate } = useUpdateRoles();

    const loadingSubmit = isCreating || isUpdate;

    const [name, setName] = useState(currentData?.name || '');
    const [selectedPermissions, setSelectedPermissions] = useState([]);

    // عند التعديل: تعبئة البيرمشن المختارة
    useEffect(() => {
        if (isEdit && currentData?.permissions) {
            const ids = currentData.permissions.map((p) => p.id);
            setSelectedPermissions(ids);
        }
    }, [isEdit, currentData]);

    // استخراج أسماء الموديولات من Permission.data
    const modules = useMemo(() => {
        const data = Permission?.data || {};
        return Object.keys(data); // مثال: ['categories','products','roles',...]
    }, [Permission]);

    const permissionsByModule = useMemo(() => Permission?.data || {}, [Permission]);

    // Helpers
    const isPermSelected = (id) => selectedPermissions.includes(id);

    const togglePermission = (id) => {
        setSelectedPermissions((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    // Check all لموديول واحد
    const toggleModuleAll = (moduleKey) => {
        const perms = permissionsByModule[moduleKey] || [];

        const moduleIds = perms.map((p) => p.id);
        const allSelected = moduleIds.every((id) => selectedPermissions.includes(id));

        setSelectedPermissions((prev) => {
            if (allSelected) {
                return prev.filter((id) => !moduleIds.includes(id));
            }
            const set = new Set(prev);
            moduleIds.forEach((id) => set.add(id));
            return Array.from(set);
        });
    };

    // Check all لكل الفورم
    const toggleAll = () => {
        const allIds = modules.flatMap((m) =>
            (permissionsByModule[m] || []).map((p) => p.id)
        );
        const allSelected =
            allIds.length > 0 && allIds.every((id) => selectedPermissions.includes(id));

        if (allSelected) {
            setSelectedPermissions([]);
        } else {
            setSelectedPermissions(allIds);
        }
    };

    const isModuleAllChecked = (moduleKey) => {
        const perms = permissionsByModule[moduleKey] || [];
        if (!perms.length) return false;
        return perms.every((p) => selectedPermissions.includes(p.id));
    };
    const isDark = theme.palette.mode === 'dark';
    const isModuleIndeterminate = (moduleKey) => {
        const perms = permissionsByModule[moduleKey] || [];
        const selectedCount = perms.filter((p) => selectedPermissions.includes(p.id))
            .length;
        return selectedCount > 0 && selectedCount < perms.length;
    };

    const isAllChecked = useMemo(() => {
        const allIds = modules.flatMap((m) =>
            (permissionsByModule[m] || []).map((p) => p.id)
        );
        return (
            allIds.length > 0 &&
            allIds.every((id) => selectedPermissions.includes(id))
        );
    }, [modules, permissionsByModule, selectedPermissions]);

    const handleSubmit = (e) => {
        e.preventDefault();

        const payload = {
            name,
            permissions: selectedPermissions,
        };

        if (isEdit && currentData?.id) {
            editRoles({ req: payload, id: currentData.id });
        } else {
            createRoles(payload);
        }
    };

    return (
        <Paper sx={{
            p: 3, mt: 2, direction: isArabic ? 'rtl' : 'ltr', backgroundColor: isDark && 'transparent'
        }}>
            <form onSubmit={handleSubmit}>
                {/* عنوان الفورم */}
                <Typography variant="h6" mb={2}>
                    {isEdit
                        ? isArabic
                            ? 'تعديل دور'
                            : 'Edit role'
                        : isArabic
                            ? 'إنشاء دور جديد'
                            : 'Create new role'}
                </Typography>

                {/* اسم الدور */}
                <Box mb={3}>
                    <TextField
                        fullWidth
                        label={isArabic ? 'اسم الدور' : 'Role name'}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                    />
                </Box>

                {/* Check all لكل الفورم */}
                <Box mb={2}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={isAllChecked}
                                indeterminate={
                                    !isAllChecked && selectedPermissions.length > 0
                                }
                                onChange={toggleAll}
                            />
                        }
                        label={
                            isArabic ? 'تحديد كل الصلاحيات' : 'Check all permissions'
                        }
                    />
                </Box>

                {/* جدول الموديولات والصلاحيات */}
                <Box
                    sx={{
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                        borderRadius: 1.5,
                        overflow: 'hidden',
                    }}
                >
                    {/* الهيدر */}
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 3fr',
                            bgcolor: (theme) =>
                                theme.palette.mode === 'dark'
                                    ? 'rgba(255,255,255,0.04)'
                                    : 'rgba(0,0,0,0.04)',
                            p: 1.5,
                        }}
                    >
                        <Typography fontWeight={600}>
                            {isArabic ? 'الموديول' : 'Module'}
                        </Typography>
                        <Typography fontWeight={600}>
                            {isArabic ? 'الصلاحيات المتاحة' : 'Available permissions'}
                        </Typography>
                    </Box>

                    {/* المحتوى */}
                    {isLoading && (
                        <Box p={3}>
                            <Typography>
                                {isArabic
                                    ? 'جاري تحميل الصلاحيات...'
                                    : 'Loading permissions...'}
                            </Typography>
                        </Box>
                    )}

                    {!isLoading && modules.length === 0 && (
                        <Box p={3}>
                            <Typography>
                                {isArabic
                                    ? 'لا توجد صلاحيات متاحة.'
                                    : 'No permissions available.'}
                            </Typography>
                        </Box>
                    )}

                    {!isLoading &&
                        modules.map((moduleKey) => {
                            const perms = permissionsByModule[moduleKey] || [];
                            if (!perms.length) return null;

                            // const moduleLabel =
                            //     moduleKey.charAt(0).toUpperCase() + moduleKey.slice(1);
                            const groupLabelKey = perms[0]?.group?.label;
                            const moduleLabel = groupLabelKey
                                ? groupLabelKey
                                : moduleKey.charAt(0).toUpperCase() + moduleKey.slice(1);


                            return (
                                <Box key={moduleKey}>
                                    <Divider />
                                    <Box sx={{ p: 2 }}>
                                        <Grid container spacing={2} alignItems="flex-start">
                                            {/* عمود الموديول + check all للموديول */}
                                            <Grid item xs={12} md={3}>
                                                <Stack spacing={1}>
                                                    <Typography fontWeight={600}>
                                                        {moduleLabel}
                                                    </Typography>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={isModuleAllChecked(moduleKey)}
                                                                indeterminate={isModuleIndeterminate(moduleKey)}
                                                                onChange={() => toggleModuleAll(moduleKey)}
                                                            />
                                                        }
                                                        label={
                                                            isArabic ? 'تحديد الكل' : 'Check all'
                                                        }
                                                    />
                                                </Stack>
                                            </Grid>

                                            {/* عمود الصلاحيات */}
                                            <Grid item xs={12} md={9}>
                                                <Grid container spacing={1}>
                                                    {perms.map((perm) => (
                                                        <Grid
                                                            item
                                                            xs={12}
                                                            sm={6}
                                                            md={4}
                                                            key={perm.id}
                                                        >
                                                            <FormControlLabel
                                                                control={
                                                                    <Checkbox
                                                                        checked={isPermSelected(perm.id)}
                                                                        onChange={() =>
                                                                            togglePermission(perm.id)
                                                                        }
                                                                    />
                                                                }
                                                                label={perm.label || perm.name}
                                                            />
                                                        </Grid>
                                                    ))}
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Box>
                            );
                        })}
                </Box>

                {/* أزرار الحفظ */}
                <Box mt={3} display="flex" justifyContent="flex-end" gap={1}>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={loadingSubmit}
                    >
                        {loadingSubmit
                            ? isArabic
                                ? 'جاري الحفظ...'
                                : 'Saving...'
                            : isEdit
                                ? isArabic
                                    ? 'تحديث'
                                    : 'Update'
                                : isArabic
                                    ? 'حفظ'
                                    : 'Save'}
                    </Button>
                </Box>
            </form>
        </Paper>
    );
};

export default FormAddEditRoles;
